// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * What the signed-in account may do (design doc §12.4), for deciding what to offer.
 *
 * * visitor (访客) — reads;
 * * engineer (维护工程师) — also files archives, records and corrects changes, scraps;
 * * administrator (管理员) — also manages device types and BOMs, reads the audit log and
 *   assigns roles.
 *
 * Only what to *show*: the server refuses what a role may not do whatever the page offers
 * (`PERMISSION_DENIED`). So while the account is not known yet — `/me` still loading, or
 * a page rendered on its own in a test — everything is offered, and a control shown by
 * mistake fails on the server rather than one hidden by mistake leaving someone stuck.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/endpoints";
import type { Role } from "@/api/types";

export interface Permissions {
  role: Role | null;
  /** File archives, record and correct changes, edit device numbers, scrap, keep drafts. */
  canRecord: boolean;
  /** Change device types and BOMs. */
  canManageMasterData: boolean;
  /** Read the audit log and assign roles. */
  isAdmin: boolean;
}

export function permissionsOf(role: Role | null): Permissions {
  return {
    role,
    canRecord: role !== "VISITOR",
    canManageMasterData: role === null || role === "ADMIN",
    isAdmin: role === null || role === "ADMIN",
  };
}

export function usePermissions(): Permissions {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false });
  return permissionsOf(me.data?.role ?? null);
}
